const destinations = [
  {
    icon: "ri-ship-line",
    title: "The Bahamas & Caribbean",
    description:
      "Caribbean cruises from Tampa — Venice's closest major cruise port at roughly 1.5 hours north — are enormously popular among Venice retirees and snowbirds. Western Caribbean itineraries hit Cozumel, Grand Cayman, and Jamaica; Eastern Caribbean reaches St. Thomas, San Juan, and the Bahamas; Southern Caribbean extends to Aruba, Curacao, and Barbados. Venice residents also enjoy easy access to Port Everglades and the Port of Miami for broader Caribbean options. The six-month validity rule applies to all cruise passengers, and Venice's high volume of first-time cruisers — many booking through retirement community travel clubs — means we see frequent passport emergencies from this destination category.",
    tags: ["Cruises", "Tampa Port", "Snowbird Travel"],
  },
  {
    icon: "ri-map-pin-2-line",
    title: "Canada",
    description:
      "Canadian travel is a cornerstone of Venice's snowbird lifestyle. Many residents maintain dual-season lives — Venice from November through April, Toronto, Ottawa, or Montreal for the summer. Direct seasonal flights from SRQ to Toronto make the transition easier than ever. Family visits to grandchildren in Vancouver, Calgary, or Halifax are common, especially around holidays. And the weak Canadian dollar has made cross-border shopping and visiting increasingly attractive for retirees watching their fixed-income budgets. The six-month validity rule for Canada is less strict than for other countries, but a valid passport is still required for all air travel.",
    tags: ["Snowbird Returns", "Family Visits", "SRQ Flights"],
  },
  {
    icon: "ri-landscape-line",
    title: "Europe",
    description:
      "European destinations draw Venice's culturally active retiree demographic for river cruises along the Rhine and Danube, theatre tours to London's West End and Stratford-upon-Avon, art tours to Florence and Paris inspired by the Ringling Museum, and Mediterranean cruises from Rome, Barcelona, and Venice, Italy. The Schengen three-month validity rule catches many retirees who assume their passport is fine because it has not technically expired. Venice's cultural community — theatre patrons, symphony subscribers, and art center members — travels to Europe more frequently than typical small-town retirees, making passport awareness especially important.",
    tags: ["River Cruises", "Theatre Tours", "Cultural Travel"],
  },
  {
    icon: "ri-treasure-map-line",
    title: "United Kingdom & Ireland",
    description:
      "The UK and Ireland appeal to Venice's heritage travelers, golf enthusiasts, and theatre devotees. Golf trips to Scotland's St. Andrews and Ireland's championship links courses are popular among retirees from Venice Golf & Country Club and Plantation Golf & Country Club. London theatre trips — often organized through Venice Theatre's travel clubs — attract patrons who want to see West End productions of plays they have performed locally. The six-month validity requirement is strict for UK entry, and we see passport emergencies from UK-bound Venice travelers every theatre season. The weak pound has made Britain increasingly budget-friendly for cost-conscious cultural travelers.",
    tags: ["Heritage", "Golf", "Theatre Tours"],
  },
  {
    icon: "ri-sun-line",
    title: "Mexico & Costa Rica",
    description:
      "Cancun, Riviera Maya, Puerto Vallarta, and Cabo San Lucas are top Mexican destinations for Venice families, retirees, and spring break travelers. Tampa International offers direct flights to Cancun and Cozumel that are convenient for Venice residents. Costa Rica's eco-tourism — zip-lining, volcano hiking, rainforest canopy tours — appeals to active retirees who hike the Legacy Trail and kayak Oscar Scherer State Park. The Mexico six-month validity rule applies to all passport holders, and the child passport trap hits particularly hard during spring break when parents book trips months in advance but forget to check their children's document expiration dates.",
    tags: ["All-Inclusive", "Eco-Tourism", "Spring Break"],
  },
  {
    icon: "ri-compass-3-line",
    title: "Panama Canal & Central America",
    description:
      "Panama Canal full-transit cruises, Costa Rica eco-tours, and Belize diving trips appeal to Venice's active retiree community. Panama Canal cruises are a bucket-list item for many affluent retirees, booking 12–18 months in advance through major cruise lines departing from Tampa, Fort Lauderdale, and Miami. Belize's barrier reef — the second largest in the world — draws diving enthusiasts from Venice who compare it to their own Gulf waters. These trips often book well in advance through retirement community travel clubs, creating a long window during which passports can expire unnoticed. The multi-country nature of Central American itineraries also means multiple entry requirements to track.",
    tags: ["Bucket List", "Diving", "Cruises"],
  },
];

const stats = [
  { value: "60%", label: "Caribbean cruise or European cultural tour requests" },
  { value: "SRQ", label: "Primary airport — 30–40 min from Venice" },
  { value: "Nov–Apr", label: "Peak snowbird & theatre tour season" },
  { value: "24 hr", label: "Shortest emergency turnaround handled" },
];

export default function VeniceDestinationsSection() {
  return (
    <section id="destinations" className="py-16 md:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <span className="inline-block text-[#B22222] text-xs font-bold uppercase tracking-[0.15em] mb-3">
            Popular Destinations
          </span>
          <h2 className="font-serif text-2xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Where Venice Residents Travel
          </h2>
          <p className="text-gray-600 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
            From Caribbean cruises out of Tampa to European theatre tours and Canadian snowbird returns — here is where Venice travelers are heading.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {destinations.map((d, i) => (
            <div
              key={i}
              className="bg-[#F8F9FB] rounded-lg p-6 border border-gray-100 hover:border-[#F6C344]/50 transition-all hover:shadow-md"
            >
              <div className="w-10 h-10 rounded-lg bg-[#1A365D] flex items-center justify-center mb-4">
                <i className={`${d.icon} text-[#F6C344] text-lg`}></i>
              </div>
              <h3 className="font-serif text-base font-bold text-[#1A365D] mb-3">{d.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">{d.description}</p>
              <div className="flex flex-wrap gap-2">
                {d.tags.map((tag, j) => (
                  <span
                    key={j}
                    className="inline-block bg-white text-[#1A365D] text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full border border-gray-200"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Stats bar */}
        <div className="bg-[#1A365D] rounded-lg p-6 md:p-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {stats.map((s, i) => (
              <div key={i}>
                <div className="text-[#F6C344] font-bold text-2xl md:text-3xl mb-1">{s.value}</div>
                <div className="text-white/70 text-xs">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}